import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button, Badge, Dropdown, Menu, Spin, notification,
  Row, Col, Typography, PageHeader, Tag, Space,
} from 'antd';
import {
  ArrowLeftOutlined, BellFilled, LoadingOutlined,
  TagOutlined,
} from '@ant-design/icons';

import moment from 'moment';
import { getApps, getApp } from 'firebase/app';
import {
  getMessaging, getToken, onMessage, MessagePayload,
} from 'firebase/messaging';
import {
  get,
  post,
  put,
  isSuperAgentError,
  RequestWrapper, ResponseWrapper,
} from '../../../dataSource/requests';
import { getUserType, getUserPermissions } from '../../../utils/users';
import { NotificationByUser, MappedNotification } from '../../../sharedTypes/NotificationTypes';
import './NotificationsComponent.scss';
import { vpdkey } from '../../../init-fcm';

const NotificationsComponent = (): JSX.Element | null => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Array<MappedNotification>>([]);
  const [showDot, setShowDot] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDropDownVisible, setIsDropDownVisible] = useState<boolean>(false);
  const [currentNotification, setCurrentNotification] = useState<MappedNotification | null>(null);
  const [show, setShow] = useState<string>('list');
  const [userType] = useState<string | null>(getUserType());

  const getUnreadNotifications = async (): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = get({
      url: '/notification/getUnreadCount',

      frontConfigs: {
        navigateFunction: navigate,
      },
    });
    try {
      response = await requestReference;
    } catch (error: unknown) {
      console.log('error', error);
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 7000):
        // "Notification count retrieved by user successfully", 7000
        if (response?.body.unreadNotificationsByUser > 0) {
          setShowDot(true);
        }
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        break;
    }
  };

  const handleNewNotification = (payload: MessagePayload): void => {
    notification.open({
      message: <span className="notification-title">{payload?.data?.['title']}</span>,

      description: (payload?.data?.['content'] && payload?.data?.['content'].length > 120)
        ? `${payload?.data?.['content']?.substring(0, 120)}...`
        : payload?.data?.['content'],

      className: 'notification-notification',
    });

    setShowDot(true);
  };

  const handleDropDownVisibleChange = async (isVisible: boolean): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    let responseSetAllRead: ResponseWrapper | undefined;
    let responseErrorSetAllRead: ResponseWrapper | undefined;
    let genericAllError: Error | undefined;

    setIsDropDownVisible(isVisible);

    if (isVisible) {
      setIsLoading(true);

      const requestReference: RequestWrapper = get({
        url: '/notification/getAllNotificationsByUser',

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        response = await requestReference;
      } catch (error: unknown) {
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      switch (true) {
        case (response?.body?.code === 7001): {
          // "All notifications by user retrieved successfully", 7001
          const newNotifications = response
            ?.body
            ?.allNotificationsByUser
            ?.map((not: NotificationByUser) => (
              {
                title: not.title,
                read: not.hasRead,
                category: not.notificationCategoryName,
                id: not.id,
                type: not.notificationType,
                dateTime: not.sendDate,
                content: not.body,
              }
            ));

          setShowDot(false);
          setIsLoading(false);
          setNotifications(newNotifications);
          setShow('list');
        } break;

        case (responseError?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          break;

        case (responseError?.body?.code === -4003):
          // "You do not have permission to perform this operation", -4003
          break;

        case (responseError?.body?.code === -4000):
          // "Something unexpected happened", -4000
          break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        default:
          break;
      }

      const setAllReadRequestReference: RequestWrapper = put({
        url: '/notification/setAllNotificationsReadForUser',

        frontConfigs: {
          navigateFunction: navigate,
        },
      });
      try {
        await setAllReadRequestReference;
      } catch (error: unknown) {
        console.log(error);
        if (isSuperAgentError(error)) {
          responseErrorSetAllRead = error.response;
        } else {
          genericAllError = error as Error;
        }
      }

      switch (true) {
        case (responseSetAllRead?.body?.code === 7002):
        case (responseSetAllRead?.body?.code === 7003):
          // "Changed all notifications status to read", 7002
          // "No notifications to change status", 7003
          break;

        case (responseErrorSetAllRead?.body?.code === -4000):
          // "Something unexpected happened", -4000
          break;

        case (responseErrorSetAllRead?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          break;

        case (responseErrorSetAllRead?.body?.code === -4003):
          // "You do not have permission to perform this operation", -4003
          break;

        case (genericAllError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        default:
          break;
      }
    }
  };

  const handleShowNotification = (_eventt: any, currentNotificationP: MappedNotification): void => {
    setShow('notification');
    setCurrentNotification(currentNotificationP);
    setIsDropDownVisible(true);
  };

  const handleNotificationClosed = (): void => {
    setShow('list');
    setCurrentNotification(null);
  };

  const defineNotType = (englishType: string | undefined): string | undefined => {
    let notType: string | undefined;

    switch (true) {
      case (englishType === 'ADDED_BY_ADMIN'):
        notType = 'Personalizada';
        break;

      case (englishType === 'SERVICES'):
        notType = 'Serviços';
        break;

      case (englishType === 'FINANCIAL'):
        notType = 'Financeiro';
        break;

      case (englishType === 'OFFERS'):
        notType = 'Promoções';
        break;

      case (englishType === 'BUDGET'):
        notType = 'Orçamento';
        break;

      case (englishType === 'MATRICULATION'):
        notType = 'Matrícula';
        break;

      default:
        notType = englishType;
        break;
    }

    return notType;
  };

  useEffect(() => {
    let intervalHandle: number;

    if (userType === 'COOPERATIVE' || userType === 'COOPERCITRUS') {
      getUnreadNotifications();
      intervalHandle = window.setInterval(getUnreadNotifications, 300000);
    }

    return () => {
      window.clearInterval(intervalHandle);
      setIsLoading(false);
    };
  }, []);

  useEffect(() => {
    const runOnMount = async (): Promise<void> => {
      let serviceWorkerRegistration;
      let pushToken;
      let permission;
      let allFirebaseApps;
      let firebaseApp;

      if (
        window.location.protocol === 'https:'

          && 'serviceWorker' in navigator
      ) {
        allFirebaseApps = getApps();

        if (!allFirebaseApps.length) {
          console.log('firebase app nao inicializado.');
          return;
        }

        firebaseApp = getApp();

        const messaging = getMessaging(firebaseApp);

        if (!('Notification' in window)) {
          console.log('This browser does not support desktop notification');
        } else if (Notification.permission === 'granted') {
          try {
            serviceWorkerRegistration = await navigator.serviceWorker.ready;
          } catch (error: unknown) {
            console.log(error);
            console.log('Failed to access serviceWorkerRegistration');
            return;
          }

          try {
            pushToken = await getToken(messaging, {
              vapidKey: vpdkey,
              serviceWorkerRegistration,
            });
          } catch (error: unknown) {
            console.log(error);
            console.log('Failed to access pushToken');
            return;
          }

          const requestBackSendTokenReference: RequestWrapper = post({
            url: '/notification/sendNotificationToken',
            params: { pushToken },

            frontConfigs: {
              navigateFunction: navigate,
            },
          });

          try {
            await requestBackSendTokenReference;
          } catch (error: unknown) {
            console.log(error);
            console.log('Failed to send token to server');
            return;
          }
        } else if (Notification.permission !== 'denied') {
          try {
            permission = await Notification.requestPermission();
          } catch (error: unknown) {
            console.log(error);
            console.log('Failed to get notification permission');
            return;
          }

          if (permission === 'granted') {
            try {
              serviceWorkerRegistration = await navigator.serviceWorker.ready;
            } catch (error: unknown) {
              console.log(error);
              console.log('Failed to access serviceWorkerRegistration');
              return;
            }

            try {
              pushToken = await getToken(messaging, {
                vapidKey: vpdkey,
                serviceWorkerRegistration,
              });
            } catch (error: unknown) {
              console.log(error);
              console.log('Failed to access pushToken');
              return;
            }

            const requestBackSendTokenReference2: RequestWrapper = post({
              url: '/notification/sendNotificationToken',
              params: { pushToken },

              frontConfigs: {
                navigateFunction: navigate,
              },
            });
            try {
              await requestBackSendTokenReference2;
            } catch (error: unknown) {
              console.log(error);
              console.log('Failed to send token to server');
              return;
            }
          } else {
            return;
          }
        } else {
          console.log('notification permission denied or dismissed');
        }

        onMessage(messaging, (payload: MessagePayload) => {
          handleNewNotification(payload);
        });
      }
    };

    runOnMount();

    return () => {
      setIsLoading(false);
    };
  }, []);

  let notificationsList = [];
  let partialNotificationsList = [];

  if (isDropDownVisible) {
    const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;
    const content = {
      key: 88888888,
      label: (
        <Row>
          <Col
            xs={{ span: 24 }}
          >
            <div className="notification-spin">
              <Spin
                indicator={antIcon}
              />
            </div>
          </Col>
        </Row>
      ),
    };

    if (show === 'list') {
      if (notifications?.length) {
        partialNotificationsList = notifications.map((not: MappedNotification) => {
          const date = `Enviada ${moment(not.dateTime).format('DD/MM/Y')} às ${moment(not.dateTime).format('HH:mm:ss')}`;

          const notType = defineNotType(not?.type);

          let divider;
          const finalElementToReturn = {
            key: not.id,
            onClick: (eventt): void => {
              handleShowNotification(eventt, not);
            },
            label: (
              <>
                <Row>
                  <Col
                    xs={{ span: 1 }}
                  >
                    {
                      !not.read
                        ? (
                          <Badge
                            className="notification-dot"
                            color="#b3d188"
                            dot
                          />
                        )
                        : null
                    }
                  </Col>
                  <Col
                    xs={{ span: 23 }}
                  >
                    <Typography.Text
                      className="notification-title"
                    >
                      {not.title}
                    </Typography.Text>
                  </Col>
                </Row>

                {
                  not?.category || not?.type
                    ? (
                      <Row>
                        <Col
                          xs={{ span: 24 }}
                        >
                          <Tag
                            color="#93bf4c"
                            icon={<TagOutlined />}
                          >
                            {
                              not?.category || notType
                            }
                          </Tag>
                        </Col>
                      </Row>
                    )
                    : null
                }

                <Row>
                  <Col
                    xs={{ span: 24 }}
                  >
                    <Typography.Text
                      className="notification-date"
                    >
                      {date}
                    </Typography.Text>
                  </Col>
                </Row>

                <Row>
                  <Col
                    xs={{ span: 24 }}
                  >
                    <Typography.Text
                      className="notification-content"
                    >
                      {
                        not.content.length > 200
                          ? `${not.content.substring(0, 200)}...`
                          : not.content
                      }
                    </Typography.Text>
                  </Col>
                </Row>
              </>
            ),
          };

          if (notifications?.length > 1) {
            divider = {
              type: 'divider',
              className: 'notification-divider',
            };
          }

          return [
            finalElementToReturn,
            divider,
          ];
        });
      } else {
        partialNotificationsList = [
          {
            key: 99999999,
            label: (
              <Row>
                <Col
                  xs={{ span: 24 }}
                >
                  <div
                    className="NoNotifications"
                  >
                    Não há notificações
                  </div>
                </Col>
              </Row>
            ),
          },
        ];
      }

      if (notifications?.length > 1) {
        notificationsList = partialNotificationsList.flat();
      } else {
        notificationsList = partialNotificationsList;
      }
    } else {
      const not = currentNotification;

      const date = `Enviada ${moment(not?.dateTime).format('DD/MM/Y')} às ${moment(not?.dateTime).format('HH:mm:ss')}`;

      const notType = defineNotType(not?.type);
      notificationsList = [
        {
          key: not?.id,
          className: 'notification-details',
          label: (
            <>
              <Row>
                <Col
                  xs={{ span: 24 }}
                >
                  <PageHeader
                    backIcon={(
                      <Space>
                        <ArrowLeftOutlined />
                        <Typography.Text className="notification-header">
                          Notificações
                        </Typography.Text>
                      </Space>
                    )}
                    className="NotificationsComponent__NotfHeader"
                    onBack={handleNotificationClosed}
                    title=" "
                  />
                </Col>
              </Row>

              <Row>
                <Col
                  xs={{ span: 24 }}
                >
                  <Typography.Text
                    className="notification-title"
                  >
                    {not?.title}
                  </Typography.Text>
                </Col>
              </Row>

              {
                not?.category || not?.type
                  ? (
                    <Row>
                      <Col
                        xs={{ span: 24 }}
                      >
                        <Tag
                          color="#93bf4c"
                          icon={<TagOutlined />}
                        >
                          {
                            not?.category || notType
                          }
                        </Tag>
                      </Col>
                    </Row>
                  )
                  : null
              }

              <Row>
                <Col
                  xs={{ span: 24 }}
                >
                  <Typography.Text
                    className="notification-date"
                  >
                    {date}
                  </Typography.Text>
                </Col>
              </Row>

              <Row>
                <Col
                  xs={{ span: 24 }}
                >
                  <Typography.Text
                    className="notification-content"
                  >
                    {not?.content}
                  </Typography.Text>
                </Col>
              </Row>
            </>
          ),
        },
      ];
    }

    if (isLoading) {
      notificationsList = [content];
    }
  }

  const menu = (
    <Menu
      className="notifications-menu-wrapper"
      items={notificationsList}
    />
  );

  return ((getUserPermissions() || []).includes('Notificações'))
    ? (
      <>
        <div className="NotificationsComponent">
          <Dropdown
            overlay={menu}
            placement="bottomRight"
            trigger={['click']}
            visible={isDropDownVisible}
            onVisibleChange={handleDropDownVisibleChange}
          >
            <Button
              className="notifications-button"
              shape="circle"
              type="link"
              size="large"
            >
              <Badge className="notifications-badge" dot={showDot}>
                <BellFilled className="notifications-icon" />
              </Badge>
            </Button>
          </Dropdown>
        </div>
      </>
    )
    : null;
};

NotificationsComponent.propTypes = {};
NotificationsComponent.defaultProps = {};

export default NotificationsComponent;
